import { create } from 'zustand';
import {
  parseSidecarListMode,
  SIDECAR_LIST_MODE_KEY,
  type SidecarListMode,
} from './sidecarBehavior';

function readStoredListMode(): SidecarListMode {
  try {
    return parseSidecarListMode(localStorage.getItem(SIDECAR_LIST_MODE_KEY));
  } catch {
    return 'stacked';
  }
}

function persistListMode(listMode: SidecarListMode): void {
  try {
    localStorage.setItem(SIDECAR_LIST_MODE_KEY, listMode);
  } catch {
    // Quota / mode privé : le mode reste en mémoire pour la session.
  }
}

interface SidecarState {
  isOpen: boolean;
  activeChannelId: string | null;
  pendingMessageId: string | null;
  pinned: boolean;
  pipActive: boolean;
  listMode: SidecarListMode;
  open: () => void;
  close: () => void;
  togglePin: () => void;
  enterPip: () => void;
  exitPip: () => void;
  setListMode: (listMode: SidecarListMode) => void;
  toggleListMode: () => void;
  selectChannel: (channelId: string, messageId?: string | null) => void;
  backToList: () => void;
  consumePendingMessageId: () => void;
}

export const useSidecarStore = create<SidecarState>((set, get) => ({
  isOpen: false,
  activeChannelId: null,
  pendingMessageId: null,
  pinned: false,
  pipActive: false,
  listMode: readStoredListMode(),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false, pipActive: false }),
  togglePin: () => set((s) => ({ pinned: !s.pinned })),
  enterPip: () => set({ pipActive: true, isOpen: true }),
  exitPip: () => set({ pipActive: false }),
  setListMode: (listMode) => {
    persistListMode(listMode);
    set({ listMode });
  },
  toggleListMode: () => {
    const listMode = get().listMode === 'split' ? 'stacked' : 'split';
    persistListMode(listMode);
    set({ listMode });
  },
  selectChannel: (channelId, messageId = null) =>
    set({ isOpen: true, activeChannelId: channelId, pendingMessageId: messageId }),
  backToList: () => set({ activeChannelId: null, pendingMessageId: null }),
  consumePendingMessageId: () => set({ pendingMessageId: null }),
}));
